import { useState } from "react";
import { Overlay } from "../../../ui/atoms/Overlay";
import { ModalActions } from "../../../ui/molecules/ModalActions";
import { useAdminResetLink } from "../../../lib/api/admin.api";
import type { UserWithCounts } from "../../../lib/api/admin.api";

interface AdminResetLinkModalProps {
  readonly user: UserWithCounts;
  readonly onClose: () => void;
}

export function AdminResetLinkModal({ user, onClose }: AdminResetLinkModalProps) {
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const generateLink = useAdminResetLink();

  const handleGenerate = () => {
    generateLink.mutate(
      { userId: user.id },
      { onSuccess: (data) => setResetLink(data.resetLink) },
    );
  };

  const handleCopy = async () => {
    if (!resetLink) return;
    await navigator.clipboard.writeText(resetLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Overlay open onClose={onClose}>
      <div className="bg-surface rounded-xl border border-border p-4 w-full max-w-sm flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-ink">
          Reset mot de passe — {user.name}
        </h3>
        <p className="text-xs text-ink-muted">{user.email}</p>

        {!resetLink ? (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-ink-muted">
              Générer un lien de réinitialisation valable 24h.
              L'utilisateur pourra choisir un nouveau mot de passe.
            </p>
            {generateLink.isError && (
              <p className="text-xs text-danger">Erreur lors de la génération</p>
            )}
            <ModalActions
              mode="create"
              hasChanges
              isSaving={generateLink.isPending}
              isValid
              onCancel={onClose}
              onSave={handleGenerate}
              saveLabel="Générer le lien"
            />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-ink-muted">Envoyez ce lien à l'utilisateur :</p>
            <div
              className="bg-surface-alt rounded-lg p-3 text-xs font-mono text-ink break-all select-all cursor-pointer border border-border"
              onClick={handleCopy}
            >
              {resetLink}
            </div>
            <div className="flex gap-2 items-center justify-between">
              <p className="text-[11px] text-amber-500">Expire dans 24h</p>
              <div className="flex gap-2">
                <button type="button" onClick={handleCopy} className="px-3 py-1.5 text-sm bg-surface-alt border border-border text-ink rounded hover:bg-border min-h-[44px] touch-feedback">
                  {copied ? "Copié !" : "Copier"}
                </button>
              </div>
            </div>
            <ModalActions
              mode="custom"
              onCancel={onClose}
              customPrimaryAction={{
                label: "Fermer",
                onClick: onClose,
                variant: "primary",
              }}
            />
          </div>
        )}
      </div>
    </Overlay>
  );
}
